import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, ArrowRight, Mail, Zap } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      toast.success(data.message || "Check your inbox");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send reset email");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" data-testid="back-login" className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center"><Zap strokeWidth={2.5} className="w-5 h-5 text-black" /></div>
          <span className="h-display text-2xl tracking-tighter">RALLY LAB</span>
        </div>
        <div className="label-mono mb-2">PASSWORD RESET</div>
        <h1 className="h-display text-4xl tracking-tighter mb-3">Forgot your password?</h1>
        <p className="text-sm text-[#A3A3A3] mb-8">
          Drop the email you registered with — we&apos;ll send you a single-use link that expires in 60 minutes.
        </p>

        {sent ? (
          <div className="card-surface p-6 border-l-4 border-[#CCFF00]" data-testid="forgot-sent">
            <Mail className="w-6 h-6 text-[#CCFF00] mb-3" />
            <h2 className="h-display text-2xl mb-2">Check your inbox.</h2>
            <p className="text-sm text-[#A3A3A3]">
              If an account with that email exists, we&apos;ve sent a reset link. Didn&apos;t get it? Check spam or wait a minute and try again.
            </p>
            <Link to="/login" className="btn-volt mt-5 px-5 py-3 text-xs inline-flex items-center gap-2">
              Back to sign in <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="label-mono mb-2">EMAIL</div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="forgot-email"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-3 text-sm focus:outline-none focus:border-[#CCFF00]"
              />
            </div>
            <button type="submit" disabled={loading} data-testid="forgot-submit"
              className="btn-volt w-full py-3.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? "Sending…" : "Email me a reset link"} <ArrowRight className="w-4 h-4" />
            </button>
            <div className="text-xs text-[#A3A3A3] text-center pt-2">
              Remembered it? <Link to="/login" className="text-[#CCFF00] hover:underline" data-testid="forgot-to-login">Sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
